import React from "react";
import _ from "underscore";
import { usePrevious, useSafeSetState } from "@evertrue/et-components";

const CACHE_SIZE_WARNING = 500;
const useCachedByIdResource = (fn, normalize = d => d) => {
  const id_tracker = React.useRef(new Set());
  const [dataById, setDataById] = useSafeSetState({});
  const [ids_loading, setIdsLoading] = useSafeSetState({});

  const value = React.useMemo(() => {
    const fetchById = idsParams => {
      const toggleIdsLoading = (ids, bool) => {
        setIdsLoading(prev => ({
          ...prev,
          ...ids.reduce((acc, id) => {
            acc[id] = bool;
            return acc;
          }, {}),
        }));
      };
      const ids = Array.isArray(idsParams) ? idsParams : [idsParams];
      if (!ids.length) return;
      toggleIdsLoading(ids, true);
      // cast to a number to ensure types match. lets users call hook w/ numbers or strings
      ids.forEach(id => id_tracker.current.add(Number(id)));

      fn(ids)
        .then(res => {
          const normalized = normalize(res, ids);
          setDataById(prev => ({ ...prev, ...normalized }));
          toggleIdsLoading(ids, false);
        })
        .catch(e => {
          toggleIdsLoading(ids, false);
          console.error(e);
        });
    };
    const fetchIfNotCached = ids => {
      if (
        (process.env.NODE_ENV === "development" && id_tracker.current.size > CACHE_SIZE_WARNING) ||
        Object.keys(dataById).length > CACHE_SIZE_WARNING
      ) {
        console.warn("Warning: useCachedByIdResource cache size over 500 objects");
      }
      const new_ids = ids.filter(id => {
        return !id_tracker.current.has(Number(id)) && !dataById[id];
      });

      if (new_ids.length) {
        new_ids.forEach(id => id_tracker.current.add(Number(id)));
        fetchById(new_ids);
      }
    };
    const invalidateCache = () => setDataById({});

    const addToCache = item => setDataById(prev => ({ ...prev, item }));
    return { fetchIfNotCached, dataById, fetchById, idsLoading: ids_loading, invalidateCache, addToCache };
  }, [dataById, fn, ids_loading, normalize, setDataById, setIdsLoading]);
  return value;
};

const useCachedByIds = (ids, fetchIfNotCachedFn) => {
  const prev_ids = usePrevious(ids);

  React.useEffect(() => {
    const new_ids = _.difference(ids, prev_ids);
    if (new_ids.length) {
      fetchIfNotCachedFn(new_ids);
    }
  }, [fetchIfNotCachedFn, ids, prev_ids]);
};

const useCachedById = (id, fetchIfNotCachedFn) => {
  React.useEffect(() => {
    if (id) {
      fetchIfNotCachedFn([id]);
    }
  }, [fetchIfNotCachedFn, id]);
};

export { useCachedByIdResource, useCachedById, useCachedByIds };
